import * as THREE from 'three';
import { useStore } from '../state/store';
import type { Entity } from './Entity';

// Procedural audio: synthesize everything in-browser so the game works with zero asset downloads.
// Real recordings can be dropped into /public/audio/ later and swapped in.

interface EnemyVoice {
  osc: OscillatorNode;
  detune: OscillatorNode;
  filter: BiquadFilterNode;
  gain: GainNode;
}

const SPEED_OF_SOUND = 240; // u/s — tuned for game scale (real ~343 m/s)
const HEAR_RANGE = 1000;
const VOICE_COUNT = 3;

export class Audio {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private engineGain!: GainNode;
  private engineOsc!: OscillatorNode;
  private engineNoise!: AudioBufferSourceNode;
  private windGain!: GainNode;
  private mgGain!: GainNode;
  private mgTick = 0;
  private unlocked = false;
  private enemyVoices: EnemyVoice[] = [];
  private _v3a = new THREE.Vector3();
  private _v3b = new THREE.Vector3();

  /** True while the tab is hidden — suppresses the auto-resume so the engine drone doesn't
   *  keep playing (and burning battery) in a background tab. */
  private tabHidden = false;

  /** Sustained UFO whir voice — a detuned drone with a vibrato LFO, started when the saucer
   *  appears and driven per-frame (pitch + volume rise as it closes) until the white-out. */
  private ufoOsc: OscillatorNode | null = null;
  private ufoOsc2: OscillatorNode | null = null;
  private ufoLfo: OscillatorNode | null = null;
  private ufoGain: GainNode | null = null;

  constructor() {
    const events = ['pointerdown', 'touchstart', 'mousedown', 'click', 'keydown'];
    const unlock = () => {
      if (!this.unlocked) {
        this.unlocked = true;
        try { this.init(); } catch (e) { console.error('[audio] init failed', e); }
      }
      // Always try to resume — the context can be re-suspended when the tab backgrounds.
      if (this.ctx && this.ctx.state !== 'running' && !this.tabHidden) {
        this.ctx.resume().catch(() => { /* ignore */ });
      }
    };
    for (const ev of events) {
      window.addEventListener(ev, unlock, { passive: true, capture: true });
    }
    // Silence the whole mix while the tab is hidden; pick the drone back up on return.
    document.addEventListener('visibilitychange', () => {
      this.tabHidden = document.hidden;
      if (!this.ctx) return;
      if (this.tabHidden) this.ctx.suspend().catch(() => { /* ignore */ });
      else this.ctx.resume().catch(() => { /* ignore */ });
    });
  }

  private init() {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctx();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.9;
    this.master.connect(this.ctx.destination);

    // Engine: sawtooth + second detuned osc for thicker rotary-engine-ish growl
    this.engineGain = this.ctx.createGain();
    this.engineGain.gain.value = 0.275;
    this.engineGain.connect(this.master);

    this.engineOsc = this.ctx.createOscillator();
    this.engineOsc.type = 'sawtooth';
    this.engineOsc.frequency.value = 95;
    const detunedOsc = this.ctx.createOscillator();
    detunedOsc.type = 'sawtooth';
    detunedOsc.frequency.value = 95 * 1.01;
    const engineFilter = this.ctx.createBiquadFilter();
    engineFilter.type = 'lowpass';
    engineFilter.frequency.value = 600;
    this.engineOsc.connect(engineFilter);
    detunedOsc.connect(engineFilter);
    engineFilter.connect(this.engineGain);
    this.engineOsc.start();
    detunedOsc.start();

    // Noise source for wind + engine rumble
    const noiseBuf = makeNoiseBuffer(this.ctx, 2);
    this.engineNoise = this.ctx.createBufferSource();
    this.engineNoise.buffer = noiseBuf;
    this.engineNoise.loop = true;
    const windFilter = this.ctx.createBiquadFilter();
    windFilter.type = 'bandpass';
    windFilter.frequency.value = 800;
    windFilter.Q.value = 0.5;
    this.windGain = this.ctx.createGain();
    this.windGain.gain.value = 0.18;
    this.engineNoise.connect(windFilter).connect(this.windGain).connect(this.master);
    this.engineNoise.start();

    // MG: noise-burst controlled gain
    this.mgGain = this.ctx.createGain();
    this.mgGain.gain.value = 0;
    const mgNoise = this.ctx.createBufferSource();
    mgNoise.buffer = noiseBuf;
    mgNoise.loop = true;
    const mgFilter = this.ctx.createBiquadFilter();
    mgFilter.type = 'bandpass';
    mgFilter.frequency.value = 1500;
    mgFilter.Q.value = 2.5;
    mgNoise.connect(mgFilter).connect(this.mgGain).connect(this.master);
    mgNoise.start();

    // Pool of voice slots for nearby-enemy engine sounds (Doppler-shifted).
    for (let i = 0; i < VOICE_COUNT; i++) {
      const osc = this.ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = 110;
      const detune = this.ctx.createOscillator();
      detune.type = 'sawtooth';
      detune.frequency.value = 113;
      const filter = this.ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 700;
      const gain = this.ctx.createGain();
      gain.gain.value = 0;
      osc.connect(filter);
      detune.connect(filter);
      filter.connect(gain).connect(this.master);
      osc.start();
      detune.start();
      this.enemyVoices.push({ osc, detune, filter, gain });
    }
  }

  /**
   * Update positional engine sounds for the N nearest enemies. Doppler is computed
   * manually: pitch_factor = c / (c + v_radial) where v_radial is the radial component
   * of (enemyVel - playerVel) along the (player → enemy) axis. Positive v_radial means
   * the enemy is moving away from the player, lowering pitch. Negative means closing,
   * raising pitch.
   */
  updateEnemyEngines(enemies: Entity[], playerPos: THREE.Vector3, playerVel: THREE.Vector3) {
    if (!this.ctx || this.enemyVoices.length === 0) return;
    // Build a sorted list of (enemy, distance) for live, audible enemies.
    const candidates: { e: Entity; d: number }[] = [];
    for (const e of enemies) {
      if (!e.alive()) continue;
      if (e.kind !== 'enemy' && e.kind !== 'zeppelin') continue;
      const d = e.position.distanceTo(playerPos);
      if (d > HEAR_RANGE) continue;
      candidates.push({ e, d });
    }
    candidates.sort((a, b) => a.d - b.d);
    const now = this.ctx.currentTime;

    for (let i = 0; i < this.enemyVoices.length; i++) {
      const v = this.enemyVoices[i];
      const slot = candidates[i];
      if (!slot) {
        v.gain.gain.setTargetAtTime(0, now, 0.12);
        continue;
      }
      const { e, d } = slot;
      // Enemy velocity from its forward × cruise speed.
      const eAny = e as unknown as { forward?: THREE.Vector3 };
      this._v3a.set(0, 0, 0);
      if (eAny.forward) {
        const speed = e.kind === 'zeppelin' ? 15 : ((e as unknown as { speed?: number }).speed ?? 62);
        this._v3a.copy(eAny.forward).multiplyScalar(speed);
      }
      // Radial axis: player → enemy (unit).
      this._v3b.copy(e.position).sub(playerPos);
      const dist = this._v3b.length();
      if (dist > 1e-3) this._v3b.multiplyScalar(1 / dist);
      // v_radial = (enemyVel - playerVel) · radial
      const vRadial = (this._v3a.x - playerVel.x) * this._v3b.x
                    + (this._v3a.y - playerVel.y) * this._v3b.y
                    + (this._v3a.z - playerVel.z) * this._v3b.z;
      const pitchFactor = SPEED_OF_SOUND / (SPEED_OF_SOUND + vRadial);
      // Different base pitch per voice slot so multiple engines don't sound identical.
      const baseFreq = e.kind === 'zeppelin' ? 55 : (95 + i * 7);
      const freq = baseFreq * pitchFactor;
      v.osc.frequency.setTargetAtTime(freq, now, 0.06);
      v.detune.frequency.setTargetAtTime(freq * 1.015, now, 0.06);
      // Distance attenuation: 1/(1 + d/300) curve, scaled by HEAR_RANGE fade.
      const distFade = Math.max(0, 1 - d / HEAR_RANGE);
      const vol = (1 / (1 + d / 220)) * distFade * 0.22;
      v.gain.gain.setTargetAtTime(vol, now, 0.08);
    }
  }

  update(maneuverIntensity = 0, engineActive = true) {
    if (!this.ctx) return;
    // Browsers can re-suspend the context when switching tabs or sometimes after an idle period.
    // Nudge it back to running so we don't end up silently dead — unless the tab is hidden
    // (we suspended it on purpose; visibilitychange resumes it on return).
    if (this.ctx.state === 'suspended' && !this.tabHidden) {
      this.ctx.resume().catch(() => { /* ignore */ });
    }
    const s = useStore.getState();
    const muted = s.muted;
    this.master.gain.setTargetAtTime(muted ? 0 : 0.9, this.ctx.currentTime, 0.05);

    // The aero engine + slipstream only exist when the player is flying a plane. In a
    // ground emplacement (AA gun, tank) they fade out so we don't hear a phantom aircraft.
    this.engineGain.gain.setTargetAtTime(engineActive ? 0.275 : 0, this.ctx.currentTime, 0.12);
    this.windGain.gain.setTargetAtTime(engineActive ? 0.18 : 0, this.ctx.currentTime, 0.12);

    // Rev engine with maneuver intensity [0..1] (hard banks/climbs audibly open up).
    const targetFreq = 92 + maneuverIntensity * 38;
    this.engineOsc.frequency.setTargetAtTime(targetFreq, this.ctx.currentTime, 0.08);

    if (s.isFiring) {
      // Simple 10Hz gain pulse
      this.mgTick += 0.016;
      const pulse = 0.5 + 0.5 * Math.sin(this.mgTick * Math.PI * 2 * 10);
      this.mgGain.gain.setTargetAtTime(0.55 * pulse, this.ctx.currentTime, 0.005);
    } else {
      this.mgGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.02);
    }
  }

  playBoom(strength = 1) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(120 * strength, t);
    osc.frequency.exponentialRampToValueAtTime(30, t + 0.5);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.9, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.8);
    osc.connect(g).connect(this.master);
    osc.start(t);
    osc.stop(t + 0.85);

    // Noise tail
    const nb = makeNoiseBuffer(this.ctx, 0.8);
    const src = this.ctx.createBufferSource();
    src.buffer = nb;
    const nf = this.ctx.createBiquadFilter();
    nf.type = 'lowpass';
    nf.frequency.value = 700;
    const ng = this.ctx.createGain();
    ng.gain.setValueAtTime(0.5, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.6);
    src.connect(nf).connect(ng).connect(this.master);
    src.start(t);
    src.stop(t + 0.7);
  }

  /** Crisp kill-confirm — a short rising blip with a metallic transient. Pitch climbs with the
   *  combo so a streak feels rewarding. Kept brief + modest so rapid kills don't overwhelm. */
  playKill(combo = 1) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const base = 520 + Math.min(combo, 8) * 60;
    const osc = this.ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(base, t);
    osc.frequency.exponentialRampToValueAtTime(base * 1.5, t + 0.07);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.28, t + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
    osc.connect(g).connect(this.master);
    osc.start(t);
    osc.stop(t + 0.18);
    // Metallic "tink" transient.
    const nb = makeNoiseBuffer(this.ctx, 0.08);
    const src = this.ctx.createBufferSource();
    src.buffer = nb;
    const nf = this.ctx.createBiquadFilter();
    nf.type = 'bandpass'; nf.frequency.value = 2600; nf.Q.value = 1.4;
    const ng = this.ctx.createGain();
    ng.gain.setValueAtTime(0.22, t);
    ng.gain.exponentialRampToValueAtTime(0.0001, t + 0.08);
    src.connect(nf).connect(ng).connect(this.master);
    src.start(t);
    src.stop(t + 0.09);
  }

  /** Air-raid klaxon — a two-wail siren when a village comes under attack. */
  playAlarm() {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'sawtooth';
    const lo = 300, hi = 460;
    osc.frequency.setValueAtTime(lo, t);
    osc.frequency.linearRampToValueAtTime(hi, t + 0.18);
    osc.frequency.linearRampToValueAtTime(lo, t + 0.36);
    osc.frequency.linearRampToValueAtTime(hi, t + 0.54);
    osc.frequency.linearRampToValueAtTime(lo, t + 0.72);
    const f = this.ctx.createBiquadFilter();
    f.type = 'lowpass'; f.frequency.value = 1500;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.3, t + 0.05);
    g.gain.setValueAtTime(0.3, t + 0.66);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.82);
    osc.connect(f).connect(g).connect(this.master);
    osc.start(t);
    osc.stop(t + 0.84);
  }

  /** Stall warning horn — a short, insistent square-wave blip the plane repeats faster (and a touch
   *  higher/louder) as it nears the stall. `urgency` 0..1 = how deep into the stall band. Accelerating
   *  beeps read as rising danger; at full urgency (mushing) it's near-continuous. */
  playStallBeep(urgency = 0) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(700 + urgency * 220, t); // 700 → 920 Hz
    const f = this.ctx.createBiquadFilter();
    f.type = 'lowpass'; f.frequency.value = 2400;
    const g = this.ctx.createGain();
    const peak = 0.11 + urgency * 0.11;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.13);
    osc.connect(f).connect(g).connect(this.master);
    osc.start(t);
    osc.stop(t + 0.15);
  }

  /** Begin the UFO whir. Two detuned sawtooths beat against each other; a slow LFO warbles the
   *  pitch (the "strange whirring"); a lowpass keeps it eerie not harsh. Volume/pitch are then
   *  driven by setUfoWhir each frame. Safe to call twice (restarts clean). */
  startUfoWhir() {
    if (!this.ctx) return;
    this.stopUfoWhir();
    const t = this.ctx.currentTime;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    const f = this.ctx.createBiquadFilter();
    f.type = 'lowpass'; f.frequency.value = 900; f.Q.value = 4;
    const o1 = this.ctx.createOscillator(); o1.type = 'sawtooth'; o1.frequency.value = 90;
    const o2 = this.ctx.createOscillator(); o2.type = 'sawtooth'; o2.frequency.value = 91.6; // beating
    // Vibrato LFO warbles both oscillators' pitch — the whirring shimmer.
    const lfo = this.ctx.createOscillator(); lfo.type = 'sine'; lfo.frequency.value = 6.5;
    const lfoGain = this.ctx.createGain(); lfoGain.gain.value = 8;
    lfo.connect(lfoGain); lfoGain.connect(o1.frequency); lfoGain.connect(o2.frequency);
    o1.connect(f); o2.connect(f); f.connect(g); g.connect(this.master);
    o1.start(t); o2.start(t); lfo.start(t);
    this.ufoOsc = o1; this.ufoOsc2 = o2; this.ufoLfo = lfo; this.ufoGain = g;
  }

  /** Drive the running whir. `intensity` 0..1: pitch climbs ~1→2.6×, LFO speeds up, volume rises. */
  setUfoWhir(intensity: number) {
    if (!this.ctx || !this.ufoGain) return;
    const t = this.ctx.currentTime;
    const base = 90 * (1 + intensity * 1.6);
    this.ufoOsc!.frequency.setTargetAtTime(base, t, 0.05);
    this.ufoOsc2!.frequency.setTargetAtTime(base * 1.018, t, 0.05);
    this.ufoLfo!.frequency.setTargetAtTime(6.5 + intensity * 10, t, 0.05);
    this.ufoGain.gain.setTargetAtTime(0.05 + intensity * 0.28, t, 0.05);
  }

  /** Stop the whir (quick fade so it doesn't click). Called at the white-out. */
  stopUfoWhir() {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    for (const o of [this.ufoOsc, this.ufoOsc2, this.ufoLfo]) { try { o?.stop(t + 0.2); } catch { /* already stopped */ } }
    if (this.ufoGain) this.ufoGain.gain.setTargetAtTime(0.0001, t, 0.05);
    this.ufoOsc = this.ufoOsc2 = this.ufoLfo = null; this.ufoGain = null;
  }

  /** The teleport punch at the white flash — a fast upward whoosh capped by a bright zap. */
  playUfoWarp() {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator(); o.type = 'sawtooth';
    o.frequency.setValueAtTime(120, t);
    o.frequency.exponentialRampToValueAtTime(2600, t + 0.35);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.35, t + 0.06);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
    const f = this.ctx.createBiquadFilter(); f.type = 'bandpass'; f.frequency.value = 1200; f.Q.value = 1.2;
    o.connect(f).connect(g).connect(this.master);
    o.start(t); o.stop(t + 0.52);
  }

  // --- Random-event cues -----------------------------------------------------------------------
  /** A low, mysterious two-note sting — something odd is about to happen. */
  playEventOmen() {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    [[196, 0], [147, 0.22]].forEach(([f, dt]) => {
      const at = t + dt;
      const o = this.ctx!.createOscillator(); o.type = 'triangle'; o.frequency.setValueAtTime(f, at);
      const flt = this.ctx!.createBiquadFilter(); flt.type = 'lowpass'; flt.frequency.value = 700;
      const g = this.ctx!.createGain();
      g.gain.setValueAtTime(0.0001, at); g.gain.exponentialRampToValueAtTime(0.22, at + 0.04); g.gain.exponentialRampToValueAtTime(0.0001, at + 0.7);
      o.connect(flt).connect(g).connect(this.master); o.start(at); o.stop(at + 0.75);
    });
  }

  /** Descending airy whoosh — a meteor tearing down through the sky (impact uses playBoom). */
  playMeteor() {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const src = this.ctx.createBufferSource();
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * 1.4, this.ctx.sampleRate);
    const d = buf.getChannelData(0); for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    src.buffer = buf;
    const f = this.ctx.createBiquadFilter(); f.type = 'bandpass'; f.Q.value = 1.2;
    f.frequency.setValueAtTime(2400, t); f.frequency.exponentialRampToValueAtTime(300, t + 1.3);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.3, t + 0.5); g.gain.exponentialRampToValueAtTime(0.0001, t + 1.35);
    src.connect(f).connect(g).connect(this.master); src.start(t); src.stop(t + 1.4);
  }

  /** A tin trench whistle — the cease-fire signal. Two short shrill blasts. */
  playWhistle() {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    [0, 0.55].forEach((dt) => {
      const at = t + dt;
      const o = this.ctx!.createOscillator(); o.type = 'sine';
      o.frequency.setValueAtTime(2100, at); o.frequency.linearRampToValueAtTime(2500, at + 0.05);
      const trem = this.ctx!.createOscillator(); trem.type = 'sine'; trem.frequency.value = 22;
      const tg = this.ctx!.createGain(); tg.gain.value = 260; trem.connect(tg); tg.connect(o.frequency);
      const g = this.ctx!.createGain();
      g.gain.setValueAtTime(0.0001, at); g.gain.exponentialRampToValueAtTime(0.16, at + 0.02); g.gain.setValueAtTime(0.16, at + 0.32); g.gain.exponentialRampToValueAtTime(0.0001, at + 0.42);
      o.connect(g).connect(this.master); o.start(at); trem.start(at); o.stop(at + 0.45); trem.stop(at + 0.45);
    });
  }

  /** Ethereal bell chime — ghosts dissolving / a crate collected. Shimmering major arpeggio. */
  playChime() {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    [784, 988, 1319, 1568].forEach((f, i) => {
      const at = t + i * 0.06;
      const o = this.ctx!.createOscillator(); o.type = 'sine'; o.frequency.value = f;
      const g = this.ctx!.createGain();
      g.gain.setValueAtTime(0.0001, at); g.gain.exponentialRampToValueAtTime(0.14, at + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, at + 0.9);
      o.connect(g).connect(this.master); o.start(at); o.stop(at + 0.95);
    });
  }

  /** Mechanical cough/rattle — gremlins in the works. Bursts of filtered noise. */
  playRattle() {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    for (let k = 0; k < 5; k++) {
      const at = t + k * 0.08 + Math.random() * 0.02;
      const src = this.ctx.createBufferSource();
      const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * 0.06, this.ctx.sampleRate);
      const d = buf.getChannelData(0); for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
      src.buffer = buf;
      const f = this.ctx.createBiquadFilter(); f.type = 'bandpass'; f.frequency.value = 180 + Math.random() * 220; f.Q.value = 6;
      const g = this.ctx.createGain(); g.gain.setValueAtTime(0.0001, at); g.gain.exponentialRampToValueAtTime(0.2, at + 0.005); g.gain.exponentialRampToValueAtTime(0.0001, at + 0.055);
      src.connect(f).connect(g).connect(this.master); src.start(at); src.stop(at + 0.06);
    }
  }

  /** A vast, slow, mournful call — the Leviathan crossing the high haze. */
  playCreatureCall() {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator(); o.type = 'sawtooth';
    o.frequency.setValueAtTime(58, t); o.frequency.linearRampToValueAtTime(72, t + 1.2); o.frequency.linearRampToValueAtTime(46, t + 2.6);
    const f = this.ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = 340; f.Q.value = 3;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.34, t + 0.5); g.gain.setValueAtTime(0.34, t + 1.8); g.gain.exponentialRampToValueAtTime(0.0001, t + 2.8);
    o.connect(f).connect(g).connect(this.master); o.start(t); o.stop(t + 2.9);
  }

  /** Rising three-note triumph chime — a wave has been cleared. */
  playWaveCleared() {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    // Major triad climb (C5-E5-G5-ish), staggered, soft triangle tones.
    [523, 659, 784].forEach((freq, i) => {
      const at = t + i * 0.13;
      const osc = this.ctx!.createOscillator();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, at);
      const g = this.ctx!.createGain();
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(0.22, at + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 0.35);
      osc.connect(g).connect(this.master);
      osc.start(at);
      osc.stop(at + 0.4);
    });
  }

  /** Low, ominous two-tone sting — a war machine has taken the field. */
  playBossSpawn() {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    // Tritone drop (the classic "danger" interval), thick saw through a dark lowpass.
    [[110, 0], [78, 0.34]].forEach(([freq, dt]) => {
      const at = t + dt;
      const osc = this.ctx!.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(freq, at);
      const f = this.ctx!.createBiquadFilter();
      f.type = 'lowpass'; f.frequency.value = 420; f.Q.value = 2;
      const g = this.ctx!.createGain();
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(0.4, at + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 0.65);
      osc.connect(f).connect(g).connect(this.master);
      osc.start(at);
      osc.stop(at + 0.7);
    });
  }

  /** Short brass-ish fanfare — a reinforcement has arrived. */
  playReinforcement() {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    // Two quick fifths (bugle call flavor): G4 → D5, square-wave softened by a lowpass.
    [[392, 0], [587, 0.12]].forEach(([freq, dt]) => {
      const at = t + dt;
      const osc = this.ctx!.createOscillator();
      osc.type = 'square';
      osc.frequency.setValueAtTime(freq, at);
      const f = this.ctx!.createBiquadFilter();
      f.type = 'lowpass'; f.frequency.value = 1600;
      const g = this.ctx!.createGain();
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(0.18, at + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 0.28);
      osc.connect(f).connect(g).connect(this.master);
      osc.start(at);
      osc.stop(at + 0.32);
    });
  }

  /** Long, room-filling hydrogen ignition — sub-bass thump + roaring noise tail. */
  playHydrogenRoar() {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    // Sub-bass drop
    const sub = this.ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(80, t);
    sub.frequency.exponentialRampToValueAtTime(20, t + 1.4);
    const subG = this.ctx.createGain();
    subG.gain.setValueAtTime(1.4, t);
    subG.gain.exponentialRampToValueAtTime(0.001, t + 2.2);
    sub.connect(subG).connect(this.master);
    sub.start(t);
    sub.stop(t + 2.3);
    // Mid growl
    const mid = this.ctx.createOscillator();
    mid.type = 'sawtooth';
    mid.frequency.setValueAtTime(200, t);
    mid.frequency.exponentialRampToValueAtTime(60, t + 1.2);
    const midF = this.ctx.createBiquadFilter();
    midF.type = 'lowpass';
    midF.frequency.value = 400;
    const midG = this.ctx.createGain();
    midG.gain.setValueAtTime(0.6, t);
    midG.gain.exponentialRampToValueAtTime(0.001, t + 1.8);
    mid.connect(midF).connect(midG).connect(this.master);
    mid.start(t);
    mid.stop(t + 1.9);
    // Wide noise roar — the gas-fire sound
    const noiseDur = 2.5;
    const nb = makeNoiseBuffer(this.ctx, noiseDur);
    const src = this.ctx.createBufferSource();
    src.buffer = nb;
    const nf = this.ctx.createBiquadFilter();
    nf.type = 'lowpass';
    nf.frequency.setValueAtTime(2400, t);
    nf.frequency.exponentialRampToValueAtTime(400, t + 2.4);
    const ng = this.ctx.createGain();
    ng.gain.setValueAtTime(0.0, t);
    ng.gain.linearRampToValueAtTime(1.1, t + 0.1);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 2.4);
    src.connect(nf).connect(ng).connect(this.master);
    src.start(t);
    src.stop(t + noiseDur);
  }
}

function makeNoiseBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
  const length = ctx.sampleRate * seconds;
  const buf = ctx.createBuffer(1, length, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < length; i++) d[i] = Math.random() * 2 - 1;
  return buf;
}
